/**
 * HtmlExport.js - HTML export for dive plans (fallback/sharing).
 *
 * Generates a standalone HTML document mirroring the PDF content,
 * used for sharing on platforms that don't support PDF, or as a
 * print-friendly alternative.
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 * AGPL-3.0-or-later - see LICENSE
 */

import { analyzeAlerts } from './alerts.js'
import {
  gasConsumptionRows,
  filterDisplaySegments,
  getMaxDepth,
  getBottomTime,
  calculateMOD,
  getStressUntilText,
  getSegmentTypeLabel,
  surfaceIntervalSentences,
  noFlyValue,
  exposureShown,
  formatDensity,
  segmentDensity,
  densityLevel
} from './planUtils.js'
import { APP_VERSION } from './SavedPlans.js'
import { Store } from '../store/Store.js'
import { escapeHtml } from './viewUtils.js'

// Environment used when the exported plan carries none: sea level, sea water.
// A plan without an environment was computed before the environment reached
// the engine, which is exactly what sea water at sea level gives; applying the
// environment currently on screen instead would print a wreck dive in the sea
// as if it had been computed for a mountain lake. Store.DEFAULT_WATER_TYPE is
// the same 'salt' preset, for the same backward compatibility reason.
const FALLBACK_ENVIRONMENT = Object.freeze({
  altitude: 0,
  waterType: Store.DEFAULT_WATER_TYPE
})

// Water an export must name: the preset label when the diver picked a preset,
// the density in g/l when a custom one was typed. Returns '--' when the stored
// water type is unusable, like the plan screen does, rather than naming a water
// the plan was not computed for. Never throws: an export must not be lost over
// a label.
export function formatExportWaterLabel(waterType, translateFn) {
  const value = (waterType === undefined || waterType === null)
    ? FALLBACK_ENVIRONMENT.waterType
    : waterType

  const preset = Store.waterPresetKey(value)
  if (preset !== Store.CUSTOM_WATER) {
    const labelKey = Store.WATER_PRESET_LABEL_KEYS[preset]
    if (!labelKey) {
      console.error('formatExportWaterLabel: no label key declared for the water preset "%s", showing the raw preset', preset)
      return preset
    }
    const label = translateFn(labelKey)
    if (label === labelKey) {
      console.error('formatExportWaterLabel: no translation for "%s", showing the raw key', labelKey)
    }
    return label
  }

  // Store.waterDensityOrNull already logs why the value is unusable
  const density = Store.waterDensityOrNull(value)
  if (density === null) {
    console.error('formatExportWaterLabel: unusable water type %o, exporting "--" instead of a water name', value)
    return '--'
  }
  return `${density} g/l`
}

// Altitude an export must show, in metres, or null when there is nothing to
// show: a dive at sea level is the common case and must not be weighed down
// with an obvious line.
export function formatExportAltitude(altitude) {
  if (altitude === undefined || altitude === null) return null

  const value = Number(altitude)
  if (!Number.isFinite(value)) {
    console.error('formatExportAltitude: unusable altitude %o, altitude left out of the export', altitude)
    return null
  }
  if (value === 0) return null

  return `${value} m`
}

// Environment rows of an export, as [label, value] pairs in display order:
// the water the plan was computed for, then the altitude when the dive is not
// at sea level. Both change the ambient pressure and therefore the whole
// profile, exactly like the gradient factors the exports already carry: on the
// planner defaults, 40 m for 30 min on air with GF 30/85 runs 87 min in fresh
// water and 93 min in the Red Sea, 45 min of stops against 51. A plan printed
// and taken underwater has to say which water it was computed for.
export function exportEnvironmentRows(environment, translateFn) {
  let source = environment
  if (!source || typeof source !== 'object') {
    console.error('exportEnvironmentRows: no usable environment (%o), exporting sea water at sea level', environment)
    source = FALLBACK_ENVIRONMENT
  }

  const environmentLabel = translateFn('export_environment')
  if (environmentLabel === 'export_environment') {
    console.error('exportEnvironmentRows: no translation for "export_environment", showing the raw key')
  }

  const rows = [[environmentLabel, formatExportWaterLabel(source.waterType, translateFn)]]

  const altitude = formatExportAltitude(source.altitude)
  if (altitude !== null) {
    const altitudeLabel = translateFn('settings_altitude')
    if (altitudeLabel === 'settings_altitude') {
      console.error('exportEnvironmentRows: no translation for "settings_altitude", showing the raw key')
    }
    rows.push([altitudeLabel, altitude])
  }

  return rows
}

/**
 * CSS class of a density cell, from the very thresholds the density alerts use.
 * A cell printed plain under an alert that fired on it would be worse than no
 * cell at all, so both read densityLevel and nothing else.
 *
 * @param {Object} segment - Plan segment
 * @param {boolean} isCCR - Whether the plan is dived on a rebreather
 * @param {Object} environment - { altitude, waterType, gasTemp }
 * @returns {string} 'critical', 'warning' or ''
 */
function densityClassHtml(segment, isCCR, environment) {
  const level = densityLevel(segmentDensity(segment, environment), isCCR)
  if (level === 'critical') return 'critical'
  if (level === 'high') return 'warning'
  return ''
}

export class HtmlExport {
  constructor(plan, settings, ccr, translateFn, bailoutGases = [], environment = {}) {
    this.plan = plan
    this.settings = settings
    this.ccr = ccr
    this.t = translateFn || ((key) => key)
    this.bailoutGases = bailoutGases
    this.environment = environment
  }

  // The carried bailout cylinders, in the order the plan puts them in the
  // diver's mouth, like the requirement table further down the document. The
  // configured order was printed as is, so the header line and the table read
  // the same four cylinders in two different orders. A cylinder the plan never
  // opens keeps its configured rank at the end of the line.
  bailoutGasesInDiveOrder(bailout) {
    const order = new Map((bailout?.gasConsumption || []).map((c, i) => [c.name, i]))
    return [...(this.bailoutGases || [])]
      .map((gas, declared) => ({
        gas,
        rank: order.has(gas.name) ? order.get(gas.name) : Number.MAX_SAFE_INTEGER,
        declared
      }))
      .sort((a, b) => (a.rank - b.rank) || (a.declared - b.declared))
      .map(entry => entry.gas)
  }

  // Generate a full standalone HTML document with dive plan details
  generateHtml() {
    const { ccr: ccrPlan, bailout } = this.plan
    const isCCR = this.ccr.enabled
    const date = new Date().toLocaleDateString()
    const time = new Date().toLocaleTimeString()

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${this.t('pdf_title')}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            color: #333;
          }
          h1 { font-size: 18px; margin-bottom: 5px; }
          h2 { font-size: 14px; margin-top: 20px; margin-bottom: 10px; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
          .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
          .date { color: #666; font-size: 11px; }
          .config { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 20px; }
          .config-item { background: #f5f5f5; padding: 8px; border-radius: 4px; }
          .config-label { font-size: 10px; color: #666; }
          .config-value { font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
          th { background: #f0f0f0; font-size: 11px; }
          td { font-size: 11px; }
          .alt-row { background: #f0f0f0; }
          .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 15px; }
          .summary-item { text-align: center; padding: 10px; background: #f5f5f5; border-radius: 4px; }
          .summary-value { font-size: 16px; font-weight: bold; }
          .summary-label { font-size: 10px; color: #666; }
          .warning { color: #856404; }
          .critical { color: #a3261c; font-weight: bold; }
          .gas-table { margin-top: 20px; }
          .footer { margin-top: 30px; text-align: center; color: #999; font-size: 10px; }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${this.t('pdf_title')}</h1>
          <div class="date">${this.t('pdf_generated')}: ${date} ${time}</div>
        </div>

        ${this.settings.diveSite?.trim() ? `
        <p style="font-size: 13px; margin-bottom: 5px;">
          <strong>${this.t('settings_dive_site')} : </strong> ${escapeHtml(this.settings.diveSite)}
        </p>
        ` : ''}

        ${this.settings.diverNames?.trim() ? `
        <p style="font-size: 13px; margin-bottom: 15px;">
          <strong>${this.t('settings_divers')} : </strong> ${escapeHtml(this.settings.diverNames)}
        </p>
        ` : ''}

        ${this.renderDiveSummaryHtml(isCCR ? ccrPlan : bailout)}

        ${isCCR && this.bailoutGases && this.bailoutGases.length > 0 ? `
        <p style="font-size: 12px; margin-bottom: 15px;">
          <strong>${this.t('gases_bailout')}:</strong> ${this.bailoutGasesInDiveOrder(bailout).map(g => `${escapeHtml(g.name)} (MOD ${calculateMOD(g, this.settings.ppO2Max)}m)`).join(' | ')}
        </p>
        ` : ''}

        <h2>${this.t('plan_config')}</h2>
        <div class="config">
          <div class="config-item">
            <div class="config-label">${this.t('settings_algorithm')}</div>
            <div class="config-value">${this.settings.algorithm.toUpperCase()}</div>
          </div>
          <div class="config-item">
            <div class="config-label">${this.t('settings_gf')}</div>
            <div class="config-value">${Math.round(this.settings.gfLow * 100)}/${Math.round(this.settings.gfHigh * 100)}</div>
          </div>
          <div class="config-item">
            <div class="config-label">${this.t('plan_mode')}</div>
            <div class="config-value">${isCCR ? 'CCR' : this.t('pdf_mode_oc')}</div>
          </div>
          <div class="config-item">
            <div class="config-label">${this.t('settings_descent')}</div>
            <div class="config-value">${this.settings.descentRate} m/min</div>
          </div>
          <div class="config-item">
            <div class="config-label">${this.t('settings_ascent')}</div>
            <div class="config-value">${this.settings.ascentRate} m/min</div>
          </div>
          <div class="config-item">
            <div class="config-label">${this.t('settings_deco_ascent')}</div>
            <div class="config-value">${this.settings.decoAscentRate} m/min</div>
          </div>
          <div class="config-item">
            <div class="config-label">${this.t('settings_last_stop_ascent')}</div>
            <div class="config-value">${this.settings.lastStopAscentRate} m/min</div>
          </div>
          <div class="config-item">
            <div class="config-label">${this.t('settings_last_stop')}</div>
            <div class="config-value">${this.settings.lastStopDepth} m</div>
          </div>
          ${isCCR ? `
          <div class="config-item">
            <div class="config-label">${this.t('settings_setpoint_mode')}</div>
            <div class="config-value">${this.ccr.mode === 'fixed' ? this.t('settings_setpoint_fixed') : this.t('settings_setpoint_dynamic')}</div>
          </div>
          <div class="config-item">
            <div class="config-label">Setpoint</div>
            <div class="config-value">${this.ccr.mode === 'fixed' ? this.ccr.fixedSetpoint : `${this.ccr.dynamicSetpoints.descent}/${this.ccr.dynamicSetpoints.bottom}/${this.ccr.dynamicSetpoints.ascent}`}</div>
          </div>
          <div class="config-item">
            <div class="config-label">${this.t('plan_diluent')}</div>
            <div class="config-value">${escapeHtml(this.ccr.diluent?.name || 'Air')}</div>
          </div>
          ` : ''}
          ${this.renderEnvironmentItemsHtml()}
        </div>

        ${isCCR && ccrPlan ? this.renderPlanSection(this.t('plan_ccr'), ccrPlan, true) : ''}

        ${bailout ? this.renderPlanSection(isCCR ? this.t('plan_bailout') : this.t('plan_deco'), bailout, false) : ''}

        ${bailout?.gasConsumption ? this.renderGasConsumption(bailout.gasConsumption, isCCR) : ''}

        ${this.settings.diveNotes?.trim() ? `
        <h2>${this.t('plan_notes')}</h2>
        <p style="white-space: pre-wrap; font-size: 11px;">${escapeHtml(this.settings.diveNotes)}</p>
        ` : ''}

        <div class="footer">
          <p>${this.t('pdf_footer_generated')} v${APP_VERSION} - https://mvplan.org</p>
          <p>${this.t('pdf_footer_verify')}</p>
          <p style="font-size: 9px; margin-top: 5px;">${this.t('disclaimer_short')}</p>
        </div>
      </body>
      </html>
    `
  }

  // Environment cells of the configuration grid: the water the plan was
  // computed for, and the altitude when the dive is not at sea level.
  // Same cell markup as the rest of the grid so the layout is unchanged.
  renderEnvironmentItemsHtml() {
    return exportEnvironmentRows(this.environment, (key, params) => this.t(key, params))
      .map(([label, value]) => `
          <div class="config-item">
            <div class="config-label">${label}</div>
            <div class="config-value">${value}</div>
          </div>
      `)
      .join('')
  }

  // Render one plan section (CCR or OC/bailout) as an HTML table
  renderPlanSection(title, plan, isCCR) {
    const segments = filterDisplaySegments(plan.segments)

    const ccrPhase = plan.ccrPhase
    const runtimeOffset = plan.runtimeOffset || 0

    // CCR phase row for bailout
    const ccrPhaseRow = ccrPhase ? `
      <tr style="background: #e6e6e6; color: #666;">
        <td>-</td>
        <td>CCR</td>
        <td>${ccrPhase.depth}m</td>
        <td>-</td>
        <td>${Math.ceil(ccrPhase.runtime)} min</td>
        <td>${escapeHtml(ccrPhase.gas)} SP${ccrPhase.setpoint}</td>
        ${isCCR ? `<td>${ccrPhase.setpoint}</td>` : ''}
        <td>${ccrPhase.ppO2?.toFixed(2) || '-'}</td>
        <td>${ccrPhase.end !== undefined && ccrPhase.end >= 0 ? ccrPhase.end + 'm' : '-'}</td>
        <td class="${densityClassHtml(ccrPhase, isCCR, this.environment)}">${formatDensity(ccrPhase, this.environment)}</td>
        <td>-</td>
      </tr>
    ` : ''

    return `
      <h2>${title}</h2>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>${this.t('plan_type')}</th>
            <th>${this.t('plan_depth')}</th>
            <th>${this.t('plan_time')}</th>
            <th>${this.t('plan_runtime')}</th>
            <th>${this.t('plan_gas')}</th>
            ${isCCR ? '<th>SP</th>' : ''}
            <th>ppO2</th>
            <th>END</th>
            <th>${this.t('plan_density')} (g/l)</th>
            <th>${this.t('plan_tissue')}</th>
          </tr>
        </thead>
        <tbody>
          ${ccrPhaseRow}
          ${segments.map((s, i) => {
            const depthDisplay = (s.type === 'ascent' || s.type === 'descent')
              ? `${s.startDepth} -> ${s.endDepth}m`
              : `${s.depth}m`
            const typeLabel = getSegmentTypeLabel(s, this.t.bind(this))
            const displayRuntime = runtimeOffset > 0 ? Math.ceil(s.runtime + runtimeOffset) : Math.ceil(s.runtime)
            const rowClass = i % 2 === 1 ? 'alt-row' : ''
            const tissueDisplay = s.tissueInfo
              ? `C${s.tissueInfo.compartment} ${s.tissueInfo.gfPercent}% ${s.tissueInfo.leadingGas}`
              : '-'

            return `
              <tr class="${rowClass}">
                <td>${i + 1}</td>
                <td>${typeLabel}</td>
                <td>${depthDisplay}</td>
                <td>${Math.ceil(s.time)} min</td>
                <td>${displayRuntime} min</td>
                <td>${escapeHtml(s.gas || '-')}</td>
                ${isCCR ? `<td>${s.setpoint || '-'}</td>` : ''}
                <td class="${s.ppO2 > 1.4 ? 'warning' : ''}">${s.ppO2?.toFixed(2) || '-'}</td>
                <td>${s.end !== undefined && s.end >= 0 ? s.end + 'm' : '-'}</td>
                <td class="${densityClassHtml(s, isCCR, this.environment)}">${formatDensity(s, this.environment)}</td>
                <td style="font-size: 10px;">${tissueDisplay}</td>
              </tr>
            `
          }).join('')}
        </tbody>
      </table>

      ${plan.stops?.length > 0 ? `
        <p><strong>${this.t('plan_stops_summary')}:</strong> ${plan.stops.map(s => `${s.depth}m: ${s.time}min`).join(' | ')}</p>
      ` : `<p>${this.t('plan_no_deco')}</p>`}

      ${this.renderAlertsHtml(segments, ccrPhase, isCCR, plan.warnings || [])}
    `
  }

  // Render alerts as styled HTML blocks (error = red, warning = yellow).
  // planWarnings carries the engine warnings of this very plan (truncated stop,
  // runtime limit, gas fallback) so the exported document states that the plan
  // is incomplete, exactly like the on screen plan does.
  renderAlertsHtml(segments, ccrPhase = null, isCCR = false, planWarnings = []) {
    const { alerts } = analyzeAlerts(segments, {
      ccrPhase,
      isCCR,
      environment: this.environment,
      translateFn: (key, params) => this.t(key, params),
      planWarnings
    })
    if (!alerts || alerts.length === 0) return ''

    const errorAlerts = alerts.filter(a => a.type === 'error')
    const warningAlerts = alerts.filter(a => a.type === 'warning')

    let html = `<h2>${this.t('alerts_summary_title')}</h2>`

    if (errorAlerts.length > 0) {
      html += `
        <div style="background: #fee2e2; border: 1px solid #dc2626; border-radius: 4px; padding: 8px; margin: 10px 0;">
          ${errorAlerts.map(a => `<div style="color: #991b1b; font-size: 11px;">${a.message}</div>`).join('')}
        </div>
      `
    }

    if (warningAlerts.length > 0) {
      html += `
        <div style="background: #fef9c3; border: 1px solid #ca8a04; border-radius: 4px; padding: 8px; margin: 10px 0;">
          ${warningAlerts.map(a => `<div style="color: #854d0e; font-size: 11px;">${a.message}</div>`).join('')}
        </div>
      `
    }

    return html
  }

  // Render the 6-cell summary grid (max depth, bottom time, runtime, TTS, CNS, OTU)
  renderDiveSummaryHtml(plan) {
    if (!plan) return ''

    const maxDepth = getMaxDepth(plan)
    const bottomTime = getBottomTime(plan)
    const runtime = Math.ceil(plan.runtime || 0)
    const tts = plan.tts || 0
    // Series figures on a repetitive dive, through the single helper
    const exposure = exposureShown(plan)

    // Surface interval: computed desaturation, then the DAN rule the no-fly
    // cell comes from
    const t = this.t.bind(this)
    const { desat, danCase, danRule, danSource } = surfaceIntervalSentences(plan, t)
    const noFly = noFlyValue(plan, t)
    const sentences = [desat, danCase, danRule, danSource].filter(Boolean).join(' ')
    const surfaceInterval = sentences
      ? `<p style="font-size: 10px; color: #666; margin: -12px 0 20px 0;">${sentences}</p>`
      : ''
    const noFlyCell = noFly
      ? `
        <div style="text-align: center; padding: 10px 5px; background: #e6f5ff; border-radius: 6px;">
          <div style="font-size: 16px; font-weight: bold; color: #005096;">${noFly}</div>
          <div style="font-size: 9px; color: #666;">${this.t('plan_no_fly')}</div>
        </div>
      `
      : ''

    return `
      <div style="display: grid; grid-template-columns: repeat(${noFly ? 7 : 6}, 1fr); gap: 8px; margin-bottom: 20px;">
        <div style="text-align: center; padding: 10px 5px; background: #e6f5ff; border-radius: 6px;">
          <div style="font-size: 16px; font-weight: bold; color: #005096;">${maxDepth}m</div>
          <div style="font-size: 9px; color: #666;">${this.t('plan_max_depth')}</div>
        </div>
        <div style="text-align: center; padding: 10px 5px; background: #e6f5ff; border-radius: 6px;">
          <div style="font-size: 16px; font-weight: bold; color: #005096;">${bottomTime}'</div>
          <div style="font-size: 9px; color: #666;">${this.t('plan_bottom_time')}</div>
        </div>
        <div style="text-align: center; padding: 10px 5px; background: #e6f5ff; border-radius: 6px;">
          <div style="font-size: 16px; font-weight: bold; color: #005096;">${runtime}'</div>
          <div style="font-size: 9px; color: #666;">${this.t('plan_runtime')}</div>
        </div>
        <div style="text-align: center; padding: 10px 5px; background: #e6f5ff; border-radius: 6px;">
          <div style="font-size: 16px; font-weight: bold; color: #005096;">${tts}'</div>
          <div style="font-size: 9px; color: #666;">${this.t('plan_tts')}</div>
        </div>
        <div style="text-align: center; padding: 10px 5px; background: #e6f5ff; border-radius: 6px;">
          <div style="font-size: 16px; font-weight: bold; color: ${exposure.cnsWarning ? '#856404' : '#005096'};">${exposure.cns}%</div>
          <div style="font-size: 9px; color: #666;">${this.t(exposure.series ? 'plan_cns_series' : 'plan_cns')}</div>
        </div>
        <div style="text-align: center; padding: 10px 5px; background: #e6f5ff; border-radius: 6px;">
          <div style="font-size: 16px; font-weight: bold; color: ${exposure.otuWarning ? '#856404' : '#005096'};">${exposure.otu}</div>
          <div style="font-size: 9px; color: #666;">${this.t(exposure.series ? 'plan_otu_series' : 'plan_otu')}</div>
        </div>
        ${noFlyCell}
      </div>
      ${surfaceInterval}
    `
  }

  // Render gas consumption table as HTML
  renderGasConsumption(consumption, isCCR) {
    if (!consumption || consumption.length === 0) return ''

    // Every cylinder is listed, the untouched ones included: hiding a gas at
    // 0 L is how a full 12 L went unmentioned on a sheet that declared another
    // cylinder short.
    const rows = gasConsumptionRows(consumption, this.t.bind(this), { shorten: false })
    if (rows.length === 0) return ''

    const title = isCCR ? this.t('plan_gas_requirements') : this.t('plan_gas_consumption')

    // RMV info line - detailed based on mode and stress settings
    let rmvInfo
    if (isCCR) {
      const stressUntilText = getStressUntilText(this.settings.stressUntil, this.t)
      rmvInfo = `${this.t('plan_based_on_rmv')}: ${this.settings.rmvStress} L/min (${stressUntilText}) -> ${this.settings.rmvDeco} L/min (deco)`
    } else {
      rmvInfo = `${this.t('plan_based_on_rmv')}: ${this.settings.rmvBottom}/${this.settings.rmvDeco} L/min`
    }

    return `
      <h2>${title}</h2>
      <table class="gas-table">
        <thead>
          <tr>
            <th>${this.t('plan_gas')}</th>
            <th>${this.t('plan_tank')}</th>
            <th>${this.t('plan_needed')}</th>
            <th>${this.t('plan_status')}</th>
          </tr>
        </thead>
        <tbody>
          ${rows.map(r => {
            const statusColor = r.insufficient
              ? '#dc2626'
              : (r.unused ? '#6b7280' : (r.tight ? '#d97706' : '#16a34a'))
            const backup = r.backupText
              ? `<div style="font-size: 9px; color: #666;">${escapeHtml(r.backupText)}</div>`
              : ''

            return `
              <tr>
                <td>${escapeHtml(r.label)}</td>
                <td>${escapeHtml(r.tankText)}</td>
                <td>${escapeHtml(r.neededText)}</td>
                <td>
                  <span style="color: ${statusColor}; font-weight: bold;">${escapeHtml(r.statusText)}</span>
                  <div style="font-size: 9px; color: #666;">${escapeHtml(r.detailText)}</div>
                  ${backup}
                </td>
              </tr>
            `
          }).join('')}
        </tbody>
      </table>
      <p style="font-size: 10px; color: #666;">${rmvInfo}</p>
    `
  }

  // Export as downloadable HTML file via <a download> link
  exportToHtml() {
    const html = this.generateHtml()
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `dive-plan-${new Date().toISOString().split('T')[0]}.html`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
}
